import React, { useState, useEffect, useCallback } from 'react';
import '../styles/ROICalculator.css';
import { sendData } from '../utils/api';

const ROICalculator = ({ onCalculate }) => {
  const [values, setValues] = useState({
    employeeCost: 25, // hourly rate in $
    employeeHours: 40, // hours saved per week
    marketingBudget: 5000, // monthly marketing budget in $
    efficiencyGain: 30, // percentage improvement
    teamSize: 5 // number of team members
  });

  const [results, setResults] = useState({
    monthlySavings: 0,
    yearlyROI: 0,
    timeReclaimed: 0,
    marketingEfficiency: 0
  });

  const [activeTab, setActiveTab] = useState('calculator');

  // Memoize the calculateROI function with useCallback
  const calculateROI = useCallback(() => {
    // Basic calculations for demo purposes
    const { employeeCost, employeeHours, marketingBudget, efficiencyGain, teamSize } = values;
    
    // Calculate time savings value
    const weeklySavings = employeeCost * employeeHours;
    const monthlySavings = weeklySavings * 4.33; // Average weeks in a month
    
    // Marketing efficiency improvements
    const marketingEfficiency = marketingBudget * (efficiencyGain / 100);
    
    // Combined monthly value
    const totalMonthlySavings = monthlySavings + marketingEfficiency;
    
    // Yearly ROI (assuming $599/mo platform cost for calculation purposes)
    const yearlyPlatformCost = 599 * 12;
    const yearlyValue = totalMonthlySavings * 12;
    const yearlyROI = ((yearlyValue - yearlyPlatformCost) / yearlyPlatformCost) * 100;
    
    // Time reclaimed per year (in hours)
    const timeReclaimed = employeeHours * 52 * teamSize; // weekly hours * weeks * team members
    
    const calculatedResults = {
      monthlySavings: totalMonthlySavings.toFixed(0),
      yearlyROI: yearlyROI.toFixed(0),
      timeReclaimed: timeReclaimed.toFixed(0),
      marketingEfficiency: marketingEfficiency.toFixed(0)
    };
    
    setResults(calculatedResults);
  }, [values]); // Only recalculate when values change

  useEffect(() => {
    calculateROI();
    // Do NOT call onCalculate here - this is causing the infinite loop
  }, [calculateROI]);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setValues({
      ...values,
      [name]: Number(value)
    });
  };

  const scrollToContact = () => {
    document.getElementById('contact-section').scrollIntoView({ behavior: 'smooth' });
  };

  // Track when users view results
  const handleViewResults = () => {
    setActiveTab('results');
    
    // This is the right place to call onCalculate - when user takes explicit action
    if (onCalculate) {
      onCalculate({
        inputs: values,
        results: results,
        resultsViewed: true
      });
    }
  };

  // Track slider changes after a delay
  const [debouncedValues, setDebouncedValues] = useState(values);
  
  useEffect(() => {
    const handler = setTimeout(() => {
      setDebouncedValues(values);
    }, 500);
    
    return () => {
      clearTimeout(handler);
    };
  }, [values]);

  // Send slider value changes to API
  useEffect(() => {
    const trackValueChange = async () => {
      try {
        await sendData('/analytics/roi-inputs', {
          updatedValues: debouncedValues,
          updateTime: new Date().toISOString()
        });
      } catch (error) {
        console.error('Error tracking ROI inputs:', error);
      }
    };
    
    // Only track if this isn't the initial render
    if (JSON.stringify(debouncedValues) !== JSON.stringify({
      employeeCost: 25,
      employeeHours: 40,
      marketingBudget: 5000,
      efficiencyGain: 30,
      teamSize: 5
    })) {
      trackValueChange();
    }
  }, [debouncedValues]);

  return (
    <section className="roi-calculator" id="roi-calculator">
      <div className="container">
        <h2>Calculate Your <span className="highlight">ROI</span></h2>
        <p className="section-subtitle">
          See how our AI agent platform can transform your business metrics
        </p>
        
        <div className="calculator-container">
          <div className="calculator-tabs">
            <button 
              className={`tab-button ${activeTab === 'calculator' ? 'active' : ''}`} 
              onClick={() => setActiveTab('calculator')}
            >
              Calculator
            </button>
            <button 
              className={`tab-button ${activeTab === 'results' ? 'active' : ''}`} 
              onClick={() => setActiveTab('results')}
            >
              Results
            </button>
          </div>
          
          <div className="calculator-body">
            {activeTab === 'calculator' ? (
              <div className="calculator-inputs">
                <div className="input-group">
                  <label>Average hourly cost per employee ($)</label>
                  <input 
                    type="range" 
                    name="employeeCost" 
                    min="15" 
                    max="100" 
                    value={values.employeeCost}
                    onChange={handleInputChange}
                  />
                  <div className="range-values">
                    <span>$15</span>
                    <span className="current-value">${values.employeeCost}</span>
                    <span>$100</span>
                  </div>
                </div>
                
                <div className="input-group">
                  <label>Hours saved per employee per week</label>
                  <input 
                    type="range" 
                    name="employeeHours" 
                    min="5" 
                    max="60" 
                    value={values.employeeHours}
                    onChange={handleInputChange}
                  />
                  <div className="range-values">
                    <span>5</span>
                    <span className="current-value">{values.employeeHours} hrs</span>
                    <span>60</span>
                  </div>
                </div>
                
                <div className="input-group">
                  <label>Monthly marketing & outreach budget ($)</label>
                  <input 
                    type="range" 
                    name="marketingBudget" 
                    min="500" 
                    max="50000" 
                    step="500"
                    value={values.marketingBudget}
                    onChange={handleInputChange}
                  />
                  <div className="range-values">
                    <span>$500</span>
                    <span className="current-value">${values.marketingBudget.toLocaleString()}</span>
                    <span>$50k</span>
                  </div>
                </div>
                
                <div className="input-group">
                  <label>Expected efficiency improvement (%)</label>
                  <input 
                    type="range" 
                    name="efficiencyGain" 
                    min="10" 
                    max="70" 
                    value={values.efficiencyGain}
                    onChange={handleInputChange}
                  />
                  <div className="range-values">
                    <span>10%</span>
                    <span className="current-value">{values.efficiencyGain}%</span>
                    <span>70%</span>
                  </div>
                </div>
                
                <div className="input-group">
                  <label>Number of team members</label>
                  <input 
                    type="range" 
                    name="teamSize" 
                    min="1" 
                    max="50" 
                    value={values.teamSize}
                    onChange={handleInputChange}
                  />
                  <div className="range-values">
                    <span>1</span>
                    <span className="current-value">{values.teamSize}</span>
                    <span>50</span>
                  </div>
                </div>
                
                <button 
                  className="view-results-button"
                  onClick={handleViewResults}
                >
                  View Your Results
                </button>
              </div>
            ) : (
              <div className="calculator-results">
                <div className="result-summary">
                  Based on your inputs, our AI agent platform could deliver:
                </div>
                
                <div className="results-grid">
                  <div className="result-card">
                    <div className="result-value">${results.monthlySavings}</div>
                    <div className="result-label">Monthly Value Generated</div>
                  </div>
                  
                  <div className="result-card highlight-result">
                    <div className="result-value">{results.yearlyROI}%</div>
                    <div className="result-label">Return on Investment (1 Year)</div>
                  </div>
                  
                  <div className="result-card">
                    <div className="result-value">{results.timeReclaimed}</div>
                    <div className="result-label">Hours Reclaimed Annually</div>
                  </div>
                  
                  <div className="result-card">
                    <div className="result-value">${results.marketingEfficiency}</div>
                    <div className="result-label">Marketing Efficiency Gains</div>
                  </div>
                </div>
                
                <div className="results-explanation">
                  <h3>What This Means For Your Business</h3>
                  <p>
                    With our AI agent platform, your business could save <strong>${results.monthlySavings} per month</strong> while 
                    reclaiming <strong>{results.timeReclaimed} hours per year</strong> to focus on growth. 
                    Your marketing dollars will go further with increased efficiency, providing an estimated 
                    <strong> {results.yearlyROI}% return on investment</strong> in the first year alone.
                  </p>
                </div>
                
                <div className="results-cta">
                  <button 
                    className="recalculate-button"
                    onClick={() => setActiveTab('calculator')}
                  >
                    Adjust Calculator
                  </button>
                  <button 
                    className="primary-button get-access-button"
                    onClick={scrollToContact}
                  >
                    Get Free Access Now
                  </button>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    </section>
  );
};

export default ROICalculator;
